/**
 * Admin Feedback Page
 * ===================
 * 
 * Admin view of all customer feedback with filtering and analytics.
 * Vibrant, professional design matching POS dashboard.
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Star,
  Download,
  TrendingUp,
  Users,
  MessageSquare,
  Filter,
  RefreshCw,
  Loader2,
  Sparkles,
  QrCode,
} from "lucide-react";

const AdminFeedbackPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [merchantFilter, setMerchantFilter] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  // Merchants list for filter dropdown
  const [merchants, setMerchants] = useState([]);

  const fetchMerchants = async () => {
    try {
      const res = await fetch(`${API}/admin/users?role=merchant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMerchants(data);
      }
    } catch (err) {
      console.error("Error fetching merchants:", err);
    }
  };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      let url = `${API}/feedback/admin/all?limit=100`;
      if (merchantFilter && merchantFilter !== "all_merchants") url += `&merchant_id=${merchantFilter}`;
      if (ratingFilter && ratingFilter !== "all") url += `&rating=${ratingFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeedback(data.feedback);
        setTotal(data.total);
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      let url = `${API}/feedback/admin/analytics`;
      if (merchantFilter && merchantFilter !== "all_merchants") url += `?merchant_id=${merchantFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  useEffect(() => {
    fetchFeedback();
    fetchAnalytics();
  }, [merchantFilter, ratingFilter, fromDate, toDate]);

  const handleExport = async () => {
    try {
      let url = `${API}/feedback/admin/export`;
      const params = [];
      if (merchantFilter && merchantFilter !== "all_merchants") params.push(`merchant_id=${merchantFilter}`);
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `feedback_export_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Export downloaded");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export");
    }
  };

  const getRatingBadge = (rating) => {
    if (rating >= 4) return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
    if (rating >= 3) return "bg-amber-500/20 text-amber-400 border-amber-500/30";
    return "bg-red-500/20 text-red-400 border-red-500/30";
  };

  const renderStars = (rating, size = "h-4 w-4") => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating 
                ? "fill-amber-400 text-amber-400 drop-shadow-[0_0_3px_rgba(251,191,36,0.5)]" 
                : "text-slate-600"
            }`}
          />
        ))}
      </div>
    );
  };

  // Calculate rating distribution for pie chart
  const getRatingDistribution = () => {
    if (!analytics?.by_merchant?.length) return null;
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    analytics.by_merchant.forEach(m => {
      dist[5] += m.rating_distribution["5"] || 0;
      dist[4] += m.rating_distribution["4"] || 0;
      dist[3] += m.rating_distribution["3"] || 0;
      dist[2] += m.rating_distribution["2"] || 0;
      dist[1] += m.rating_distribution["1"] || 0;
    });
    return dist;
  };

  const ratingDist = getRatingDistribution();
  const totalRatings = ratingDist ? Object.values(ratingDist).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              Customer Feedback
            </h1>
            <p className="text-slate-400 text-sm mt-2 ml-14">Monitor ratings and reviews from your customers</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => { fetchFeedback(); fetchAnalytics(); }} 
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleExport} className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg shadow-indigo-500/25">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <MessageSquare className="h-5 w-5 text-indigo-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Total Feedback</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{analytics?.overall?.total_feedback || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Star className="h-5 w-5 text-amber-500" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Average Rating</span>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-3xl font-bold text-slate-800">{analytics?.overall?.avg_rating || "-"}</p>
              {analytics?.overall?.avg_rating && renderStars(Math.round(analytics.overall.avg_rating), "h-5 w-5")}
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Positive (4-5★)</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">
              {analytics?.by_merchant?.reduce((sum, m) => sum + (m.rating_distribution["4"] || 0) + (m.rating_distribution["5"] || 0), 0) || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Active Merchants</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{analytics?.by_merchant?.length || 0}</p>
          </div>
        </div>

        {/* Rating Distribution + Filters Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Rating Distribution */}
          <div className="bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Rating Distribution
            </h3>
            {totalRatings > 0 ? (
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = ratingDist?.[star] || 0;
                  const percent = totalRatings > 0 ? (count / totalRatings * 100) : 0;
                  const barColor = star >= 4 ? "bg-emerald-500" : star === 3 ? "bg-amber-500" : "bg-red-500";
                  return (
                    <div key={star} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-600 w-8">{star}★</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${barColor} rounded-full transition-all duration-500`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-500 w-10 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-sm text-center py-4">No ratings yet</p>
            )}
          </div>

          {/* Filters */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-lg shadow-slate-900/10 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Filter className="h-4 w-4 text-indigo-500" />
              Filters
            </h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-48">
                <label className="text-xs text-slate-500 block mb-1.5 font-medium">Merchant</label>
                <Select value={merchantFilter} onValueChange={setMerchantFilter}>
                  <SelectTrigger className="bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All merchants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_merchants">All merchants</SelectItem>
                    {merchants.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.business_name || `${m.first_name} ${m.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-36">
                <label className="text-xs text-slate-500 block mb-1.5 font-medium">Rating</label>
                <Select value={ratingFilter} onValueChange={setRatingFilter}>
                  <SelectTrigger className="bg-slate-50 border-slate-200">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ratings</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <label className="text-xs text-slate-500 block mb-1.5 font-medium">From Date</label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              
              <div className="w-40">
                <label className="text-xs text-slate-500 block mb-1.5 font-medium">To Date</label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              
              <Button
                variant="outline"
                onClick={() => {
                  setMerchantFilter("");
                  setRatingFilter("all");
                  setFromDate("");
                  setToDate("");
                }}
                className="border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>

        {/* Feedback Cards */}
        <div className="bg-white rounded-2xl shadow-lg shadow-slate-900/10 border border-slate-100 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">All Feedback ({total})</h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                <QrCode className="h-10 w-10 text-indigo-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No feedback yet</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Share the QR code on your receipts to start collecting customer feedback!
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {feedback.map((fb) => (
                <div key={fb.feedback_id} className="p-5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Rating Badge */}
                    <div className={`px-3 py-2 rounded-xl border ${getRatingBadge(fb.rating)} flex flex-col items-center min-w-[70px]`}>
                      <span className="text-2xl font-bold">{fb.rating}</span>
                      <div className="flex gap-0.5 mt-1">
                        {renderStars(fb.rating, "h-3 w-3")}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium text-slate-800">{fb.merchant_name}</span>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="text-sm text-slate-500">
                          {new Date(fb.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                          })}
                        </span>
                      </div>
                      {fb.comment ? (
                        <p className="text-slate-600 leading-relaxed">"{fb.comment}"</p>
                      ) : (
                        <p className="text-slate-400 italic text-sm">No comment provided</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2 font-mono">
                        Transaction: {fb.transaction_id}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Merchant Breakdown */}
        {analytics?.by_merchant?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-900/10 border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800">Feedback by Merchant</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Merchant</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wider">5★</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wider">4★</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-amber-500 uppercase tracking-wider">3★</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-orange-500 uppercase tracking-wider">2★</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-red-500 uppercase tracking-wider">1★</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {analytics.by_merchant.map((m) => (
                    <tr key={m.merchant_id} className="hover:bg-slate-50/50">
                      <td className="px-5 py-4">
                        <p className="font-medium text-slate-800">{m.merchant_name}</p>
                      </td>
                      <td className="px-3 py-4 text-center font-semibold text-slate-700">{m.total_feedback}</td>
                      <td className="px-3 py-4 text-center">
                        <span className={`font-bold ${m.avg_rating >= 4 ? "text-emerald-600" : m.avg_rating >= 3 ? "text-amber-500" : "text-red-500"}`}>
                          {m.avg_rating}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-center text-emerald-600 font-medium">{m.rating_distribution["5"] || 0}</td>
                      <td className="px-3 py-4 text-center text-emerald-600 font-medium">{m.rating_distribution["4"] || 0}</td>
                      <td className="px-3 py-4 text-center text-amber-500 font-medium">{m.rating_distribution["3"] || 0}</td>
                      <td className="px-3 py-4 text-center text-orange-500 font-medium">{m.rating_distribution["2"] || 0}</td>
                      <td className="px-3 py-4 text-center text-red-500 font-medium">{m.rating_distribution["1"] || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedbackPage;
